<!-- views/players/Players.vue -->
<template>
  <div id="list">
      <div
        :class="[
          estimate == null && estimate < estimateMinimum ? '' : 'hide',
          { error },
        ]"
        ref="estimation-body"
        class="estimate"
      >
        <template v-if="estimate == null">
          <template v-if="teamCategoryName != 'INTEGRA'">
            <div class="info">
              <p class="text">{{ $t("params.ask_estimated_players") }}</p>
              <span
                class="message_error"
                v-html="$t('errors.estimate_players', { s: estimateMinimum })"
              ></span>
            </div>
            <div class="actions">
              <input
                class="value"
                type="number"
                min="1"
                v-model="inputEstimatePlayers"
              />
              <button class="update-button" v-on:click="onUpdate()">
                {{ $t("update") }}
              </button>
            </div>
          </template>
          <template v-else>
            <div class="info">
              <p class="text">
                {{ $t("params.ask_estimated_players") }}
              </p>
              <span
                class="message_error"
                v-html="
                  $t('errors.estimate_players_maximum', { s: estimateMinimum })
                "
              ></span>
            </div>
            <div class="actions">
              <input
                class="value"
                type="number"
                min="1"
                v-model="inputEstimatePlayers"
              />
              <button class="update-button" v-on:click="onUpdate()">
                {{ $t("update") }}
              </button>
            </div>
          </template>
        </template>

        <template v-else>
          <div>
            <p class="text">{{ $t("descriptions.teams_zone_estimations") }}</p>
          </div>
        </template>
      </div>
    <GenericList
      :config="config.players"
      :persons="persons"
      :team="team"
      :error="error"
      :showEstimationDialog="showEstimationDialog"
      :showTransferDialog="showTransferDialog"
      :categories="categories"
      :playersException="playersException"
      :transferError="transferError"
      :isTransferring="isTransferring"
      @load="load"
      @remove="onRemove"
      @update="onUpdate"
      @transfer="onTransfer"
      @error="onError"
      @toggle-estimation-dialog="showEstimationDialog = $event"
      @toggle-transfer-dialog="showTransferDialog = $event"
    />
  </div>
</template>

<script>
// Component Hierarchy Overview
// Players.vue
// └── GenericList.vue
//     └── Header.vue
//     │   ├── HeaderBanner.vue                ← displays top banner with logo, background, and event color
//     │   ├── HeaderMain.vue                  ← main section with user info, team switcher, and event links
//     │   │   ├── HeaderUserInfo.vue          ← user name, logout, profile actions
//     │   │   ├── HeaderTeamSelect.vue        ← team switcher dropdown and team badge
//     │   │   ├── HeaderEventLinks.vue        ← buttons for rules, calendar, groups, system
//     │   └── HeaderNav.vue                   ← optional nav bar (e.g., back button or breadcrumb)
//     │       └── HeaderBackButton.vue        ← navigation/back functionality
//     └── PersonTable.vue
//         ├── RepresentativeInfo.vue          ← if config.hasRepresentative is true
//         ├── EstimationPrompt.vue            ← if showEstimationError is true
//         ├── CounterDisplay.vue              ← shows current/total count
//         ├── SearchInput.vue                 ← search bar
//         ├── DataTableWrapper.vue            ← displays the v-data-table
//         ├── ConfirmPopup.vue                ← shown on delete confirmation
//         ├── EstimationDialog.vue            ← modal for editing estimate
//         └── TeamTransferDialog.vue          ← modal for transferring players
import GenericList from '@/components/ui/generic-list/GenericList.vue'
import { entityListConfig as config } from '@/config/entity-list-config.js'

export default {
  name: 'PlayersView',
  components: {
    GenericList
  },
  data () {
    return {
      persons: [],
      team: {},
      error: false,
      showEstimationDialog: false,
      showTransferDialog: false,
      transferError: '',
      isTransferring: false,
      showEstimationBanner: false,
      estimateMinimum: null,
      estimate: null,
      inputEstimatePlayers: ''
    }
  },
  computed: {
    teamCategoryName () {
      if (
        this.team &&
        this.team.team &&
        this.team.team.category &&
        this.team.team.category.name
      ) {
        return this.team.team.category.name
      }
      return ''
    },
    config () {
      const cfg = {
        ...config,
        players: {
          ...config.players,
          columns: [...config.players.columns]
        }
      }

      // Show "transfer" only for backoffice users
      cfg.players.columns = this.userIsBackOffice
        ? cfg.players.columns
        : cfg.players.columns.filter(col => col.action !== 'transfer')

      return cfg
    },
    routeType () {
      return this.$route.name
    },
    club () {
      return this.$store.getters['club/getClub']
    },
    userIsBackOffice () {
      return this.$store.getters['user/getUserIsBackOffice']
    },
    categories () {
      const club = this.club || {}
      const teams = Array.isArray(club.teams)
        ? club.teams
        : club.teams && typeof club.teams === 'object'
          ? Object.values(club.teams)
          : []

      const res = teams.reduce((groups, team) => {
        const { id, category } = team
        const catId = category.id
        if (!groups[catId]) groups[catId] = {}
        groups[catId][id] = team
        return groups
      }, {})
      return res
    },
    playersException () {
      return this.$store.getters['team/getNumPlayersException']
    }
  },
  methods: {
    load () {
      const { team_id: teamId } = this.$route.params
      const user = this.$store.getters['user/getUser']

      const loadClub = user.admin
        ? this.$store.dispatch('club/loadClubEvent', {
          club_id: user.club_id,
          event_id: user.event_id
        })
        : Promise.resolve()

      loadClub
        .then(() => {
          return this.$store.dispatch('team/loadTeam', { team_id: teamId })
        })
        .then(() => {
          const teamData = this.$store.getters['team/getTeam']
          this.team = teamData
          this.estimate = teamData.team.estimate_players ? parseInt(teamData.team.estimate_players) : null
          this.estimateMinimum = parseInt(teamData.team.category.minimum_players)
          this.inputEstimatePlayers = teamData.team.estimate_players
          this.persons = Object.values(teamData.players || {})
        })
    },
    onRemove (id) {
      this.persons = this.persons.filter(p => p.id !== id)
    },
    onError (error) {
      this.error = error
    },
    isInvalidPlayerEstimate (inputValue, min, cat) {
      const val = parseInt(inputValue)
      const isIntegra = cat.name === 'INTEGRA'

      const belowMinimum = val < min
      const aboveMaximum = val > min

      if (!isIntegra && belowMinimum) return true
      if (isIntegra && aboveMaximum) return true

      return false
    },
    onUpdate () {
      const type = 'players'
      const teamData = this.team.team
      const category = teamData.category
      if (!teamData) return

      const estimatePlayers = this.inputEstimatePlayers
      const estimateMinimum = +category.minimum_players

      if (this.isInvalidPlayerEstimate(estimatePlayers, estimateMinimum, category)) {
        this.error = true
        return
      }
      this.error = false

      const currentValue = +teamData[`estimate_${type}`]
      if (currentValue === estimatePlayers) {
        this.closeEstimation()
        return
      }

      const updatedInput = {
        estimate_players: estimatePlayers,
        estimate_staff: teamData.estimate_staff,
        estimate_acompanyants: teamData.estimate_acompanyants
      }

      this.$store
        .dispatch('team/updateEstimatePlayers', {
          team_id: teamData.id,
          input: updatedInput
        })
        .then(() => {
          this[`input_estimate_${type}`] = ''
          this.load()
          this.closeEstimation()

          if (this.user && !this.user.admin) {
            this.$store.dispatch('user/loadUser')
          }
        })
    },
    onTransfer (playerId, teamId) {
      this.transferError = ''
      this.isTransferring = true

      this.$store
        .dispatch('person/transferPerson', {
          team_id: teamId,
          person_id: playerId
        })
        .then((data) => {
          if (!data.status) {
            this.transferError = data.error.message
          } else {
            this.showTransferDialog = false
            this.load()
          }
        })
        .catch(err => {
          this.transferError = err.message || 'Transfer failed'
        })
        .finally(() => {
          this.isTransferring = false
        })
    },
    closeEstimation () {
      this.showEstimationDialog = false
      this.error = false
    }
  },
  created () {
    this.load()
  },
  watch: {
    $route: 'load'
  }
}
</script>

<style lang="scss" scoped>
#app {
  background-color: #fff;
  margin: 0;
  padding: 0;
  height: 100%;
}
</style>

<style lang="scss" src="@/styles/_person_list.scss" scoped>
/* you can leave this empty or add component-specific overrides */
</style>
